import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { SidebarConsumerRenderProps, Views, withSidebarContext } from 'components/sidebar/SidebarContext';
import SidebarItem from 'components/sidebar/SidebarItem';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Container, Icon, Menu } from 'semantic-ui-react';
import styled from 'styled-components';
import { Module, Privilege } from 'ts-types/api.enums';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledMenu = styled(Menu)`
  &.ui.menu {
    margin-bottom: 0;
    border-radius: unset;
    background-color: #2D404A;
    overflow: auto;
    height: 100vh;

    &.vertical.icon {
      width: inherit;
    }
  }
`;

const StyledMenuItem = styled(Menu.Item)`
  min-width: 0 !important;
`;

const StyledIconGroup = styled(Icon.Group)`
  margin: 0 auto .5rem !important;
`;

const StyledContainer = styled(Container)`
  font-size: 1.71428571em !important;
  margin: 0 !important;
  transition: all 0.35s;
  ${props => props.compact ? "transform: rotate(180deg)" : ""};
`;


interface Props extends RouterProps, AuthConsumerRenderProps, SidebarConsumerRenderProps, MessageSourceProps {
}

interface State {
  activeTab: string
}

class SidebarMenu extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {history, setActiveTab} = this.props;

    setActiveTab(history.location.pathname);

    this.state = {
      activeTab: Views.HOME
    }
  }

  componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any): void {
    if (nextProps.activeTab != this.state.activeTab) {
      this.setState({
        activeTab: nextProps.activeTab
      })
    }
  }

  patientEntryIcon = () => {
    return (
        <StyledIconGroup size="large">
          <Icon name="address book"/>
          {/*<Icon corner name={'phone'} inverted className={"top right"}/>*/}
        </StyledIconGroup>
    );
  };

  labCentralIcon = () => {
    return (
        <StyledIconGroup size="large">
          <Icon name="lab"/>
          {/*<Icon corner name={'phone'} inverted className={"top right"}/>*/}
        </StyledIconGroup>
    );
  };

  dashboardIcon = () => {
    return (
        <StyledIconGroup size="large">
          <Icon name="dashboard"/>
          {/*<Icon corner name={'phone'} inverted className={"top right"}/>*/}
        </StyledIconGroup>
    );
  };

  userSettingIcon = () => {
    return (
        <StyledIconGroup size="large">
          <Icon name="user"/>
          <Icon corner name={'setting'} rotated="clockwise" inverted className={"top right"}/>
        </StyledIconGroup>
    );
  };

  onTabClick = (route: string, view: Views) => {

    const {history, setActiveTab} = this.props;

    setActiveTab(view);
    history.push(route);
  };

  render(): React.ReactNode {

    const {
      getMessage,
      compactView,
      toggleSidebar,
      currentUserHasPrivilege,
      currentUser,
      moduleEnabledForUser,
      moduleAccessible
    } = this.props;
    const {activeTab} = this.state;

    const userRoles = currentUser ? currentUser.roles : [];
    const isMandateAdmin = userRoles.includes("ROLE_MANDATE_ADMIN");
    const isAppAdmin = userRoles.includes("ROLE_APP_ADMIN");

    const userSettingPrivilege = currentUserHasPrivilege(Privilege.PX_CORE_USER_SETTINGS_CONFIGURATION)
        && (isMandateAdmin || isAppAdmin);

    const onHomeTabClicked = () => this.onTabClick("/", Views.HOME);
    const onClinicalDataTabClicked = () => this.onTabClick("/clinical-data-record", Views.CLINICAL_DATA_RECORD);
    const onPatientEntryTabClicked = () => this.onTabClick("/patient-central", Views.PATIENT_CENTRAL);
    const onLabCentralTabClicked = () => this.onTabClick("/lab-central/testkit-reception", Views.LAB_CENTRAL);
    const onDashboardTabClicked = () => this.onTabClick("/dashboard", Views.DASHBOARD);
    const onGuarantorsTabClicked = () => this.onTabClick("/guarantors", Views.GUARANTORS);
    const onDoctorsTabClicked = () => this.onTabClick("/doctors", Views.DOCTORS);
    const onTariffTabClicked = () => this.onTabClick("/tariff", Views.TARIFF);
    const onMedicamentsTabClicked = () => this.onTabClick("/medicaments", Views.MEDICAMENTS);
    const onBillingTabClicked = () => this.onTabClick("/billing/bill-texts", Views.BILLING);
    const onSettingsTabClicked = () => this.onTabClick("/settings/countries", Views.SETTINGS);
    const onUserSettingsTabClicked = () => this.onTabClick(userSettingPrivilege ?
        "/user-settings/users" : "/user-settings/mandates", Views.USER_SETTINGS);

    return (
        <StyledMenu stackable inverted vertical icon="labeled">
          <StyledMenuItem link onClick={toggleSidebar}>
            <StyledContainer as="div" compact={compactView}>
              <Icon name={'arrow left'}/>
            </StyledContainer>
          </StyledMenuItem>
          <SidebarItem
              icon={"home"}
              title={getMessage("header.navigation.home")}
              enabled={moduleAccessible(Module.MODULE_HOME)}
              active={activeTab == Views.HOME}
              onClick={onHomeTabClicked}
          />
          <SidebarItem
              icon={"drivers license"}
              title={getMessage("header.navigation.clinicalDataRecord")}
              enabled={moduleAccessible(Module.MODULE_CDR)}
              active={activeTab == Views.CLINICAL_DATA_RECORD}
              onClick={onClinicalDataTabClicked}
          />
          <SidebarItem
              iconComponent={this.patientEntryIcon()}
              title={getMessage("header.navigation.patientEntry")}
              enabled={moduleAccessible(Module.MODULE_PATIENT_CENTRAL)}
              active={activeTab == Views.PATIENT_CENTRAL}
              onClick={onPatientEntryTabClicked}
          />
          <SidebarItem
              iconComponent={this.labCentralIcon()}
              title={getMessage("header.navigation.labCentral")}
              enabled={moduleAccessible(Module.MODULE_LAB)}
              active={activeTab == Views.LAB_CENTRAL}
              onClick={onLabCentralTabClicked}
          />
          <SidebarItem
              iconComponent={this.dashboardIcon()}
              title={getMessage("header.navigation.dashboard")}
              enabled={moduleAccessible(Module.MODULE_TESTING)}
              active={activeTab == Views.DASHBOARD}
              onClick={onDashboardTabClicked}
          />
          <SidebarItem
              icon={"building outline"}
              title={getMessage("header.navigation.guarantors")}
              enabled={moduleAccessible(Module.MODULE_GUARANTORS)}
              active={activeTab == Views.GUARANTORS}
              onClick={onGuarantorsTabClicked}
          />
          <SidebarItem
              icon={"user doctor"}
              title={getMessage("header.navigation.doctors")}
              enabled={moduleAccessible(Module.MODULE_DOCTORS)}
              active={activeTab == Views.DOCTORS}
              onClick={onDoctorsTabClicked}
          />
          <SidebarItem
              icon={"credit card"}
              title={getMessage("sidebar.navigation.tariff")}
              enabled={moduleAccessible(Module.MODULE_TARIFF)}
              active={activeTab == Views.TARIFF}
              onClick={onTariffTabClicked}
          />
          <SidebarItem
              icon={"pills"}
              title={`${getMessage("sidebar.navigation.medAndMaterial.medicaments")}/\n${getMessage("sidebar.navigation.medAndMaterial.materials")}`}
              enabled={moduleAccessible(Module.MODULE_MEDICINES_MATERIALS)}
              active={activeTab == Views.MEDICAMENTS}
              onClick={onMedicamentsTabClicked}
          />
          <SidebarItem
              icon={"money bill alternate outline"}
              title={getMessage("sidebar.navigation.billing")}
              active={activeTab == Views.BILLING}
              enabled={currentUserHasPrivilege(Privilege.PX_CORE_BILLS)
                  && moduleEnabledForUser(Module.MODULE_BILLING)}
              onClick={onBillingTabClicked}
          />
          <SidebarItem
              icon={"settings"}
              title={getMessage("sidebar.navigation.settings")}
              enabled={moduleAccessible(Module.MODULE_SETTINGS)}
              active={activeTab == Views.SETTINGS}
              onClick={onSettingsTabClicked}
          />
          <SidebarItem
              iconComponent={this.userSettingIcon()}
              title={getMessage("sidebar.navigation.userSettings")}
              enabled={moduleAccessible(Module.MODULE_USER_SETTINGS)}
              active={activeTab == Views.USER_SETTINGS}
              onClick={onUserSettingsTabClicked}
          />
        </StyledMenu>
    );
  }
}

export default withRouterWorkaround(withAuthContext(withSidebarContext(withMessages(SidebarMenu))));
